import { createGlobalStyle } from 'styled-components';
import { lightTheme } from './theme';

/**
 * Global styles for the portfolio website
 * Includes Google Fonts import and base styling
 */

// Import Google Fonts
const GlobalStyles = createGlobalStyle<{ theme: typeof lightTheme }>`
  /* Fonts moved to index.html to avoid @import warnings */

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
    font-size: 16px;
  }

  /* Anti-scraping protection styles */
  .protected-text {
    user-select: none !important;
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
    -webkit-touch-callout: none !important;
    -webkit-user-drag: none !important;
    -khtml-user-select: none !important;
  }
  
  .protected-text::selection {
    background: transparent !important;
  }
  
  .protected-text::-moz-selection {
    background: transparent !important;
  }

  /* Disable right-click on sensitive elements */
  .no-context-menu {
    -webkit-context-menu: none !important;
    -moz-context-menu: none !important;
    context-menu: none !important;
  }

  /* Honeypot styles */
  .honeypot {
    position: absolute !important;
    left: -9999px !important;
    top: -9999px !important;
    width: 1px !important;
    height: 1px !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }

  body {
    font-family: ${props => props.theme.typography.fonts.primary};
    font-size: ${props => props.theme.typography.sizes.base};
    font-weight: ${props => props.theme.typography.weights.normal};
    line-height: ${props => props.theme.typography.lineHeights.normal};
    color: ${props => props.theme.colors.text};
    background-color: ${props => props.theme.colors.background};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;
  }

  /* Custom scrollbar styling */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.surface};
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.primary[500]};
    border-radius: ${props => props.theme.borderRadius.full};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.primary[600]};
  }

  /* Selection styling */
  ::selection {
    background-color: ${props => props.theme.colors.primary[200]};
    color: ${props => props.theme.colors.primary[900]};
  }

  ::-moz-selection {
    background-color: ${props => props.theme.colors.primary[200]};
    color: ${props => props.theme.colors.primary[900]};
  }

  /* Focus styles for accessibility */
  *:focus {
    outline: 2px solid ${props => props.theme.colors.primary[500]};
    outline-offset: 2px;
  }

  /* Remove focus outline for mouse users */
  *:focus:not(:focus-visible) {
    outline: none;
  }

  /* Typography styles */
  h1, h2, h3, h4, h5, h6 {
    font-family: ${props => props.theme.typography.fonts.heading};
    font-weight: ${props => props.theme.typography.weights.bold};
    line-height: ${props => props.theme.typography.lineHeights.tight};
    color: ${props => props.theme.colors.text};
  }

  h1 {
    font-size: ${props => props.theme.typography.sizes['5xl']};
  }

  h2 {
    font-size: ${props => props.theme.typography.sizes['4xl']};
  }

  h3 {
    font-size: ${props => props.theme.typography.sizes['3xl']};
  }

  h4 {
    font-size: ${props => props.theme.typography.sizes['2xl']};
  }

  h5 {
    font-size: ${props => props.theme.typography.sizes.xl};
  }

  h6 {
    font-size: ${props => props.theme.typography.sizes.lg};
  }

  p {
    margin-bottom: ${props => props.theme.spacing[4]};
    color: ${props => props.theme.colors.textSecondary};
  }

  /* Link styles */
  a {
    color: ${props => props.theme.colors.primary[600]};
    text-decoration: none;
    transition: color ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};
  }

  a:hover {
    color: ${props => props.theme.colors.primary[500]};
    text-decoration: underline;
  }

  /* Button reset */
  button {
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
  }

  /* Image styles */
  img {
    max-width: 100%;
    height: auto;
  }

  /* Code styling */
  code {
    font-family: ${props => props.theme.typography.fonts.mono};
    font-size: ${props => props.theme.typography.sizes.sm};
    background-color: ${props => props.theme.colors.surface};
    padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[2]};
    border-radius: ${props => props.theme.borderRadius.base};
    color: ${props => props.theme.colors.primary[600]};
  }

  pre {
    font-family: ${props => props.theme.typography.fonts.mono};
    background-color: ${props => props.theme.colors.surface};
    padding: ${props => props.theme.spacing[4]};
    border-radius: ${props => props.theme.borderRadius.lg};
    overflow-x: auto;
    margin: ${props => props.theme.spacing[4]} 0;
  }

  /* Utility classes */
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 ${props => props.theme.spacing[4]};
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Animation classes */
  .fade-in {
    opacity: 0;
    animation: fadeIn 0.6s ease-in-out forwards;
  }

  .slide-up {
    opacity: 0;
    transform: translateY(20px);
    animation: slideUp 0.6s ease-out forwards;
  }

  .slide-down {
    opacity: 0;
    transform: translateY(-20px);
    animation: slideDown 0.6s ease-out forwards;
  }

  .scale-in {
    opacity: 0;
    transform: scale(0.9);
    animation: scaleIn 0.5s ease-out forwards;
  }

  @keyframes fadeIn {
    to {
      opacity: 1;
    }
  }

  @keyframes slideUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideDown {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes scaleIn {
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  /* Responsive utilities */
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    .container {
      padding: 0 ${props => props.theme.spacing[4]};
    }

    h1 {
      font-size: ${props => props.theme.typography.sizes['4xl']};
    }

    h2 {
      font-size: ${props => props.theme.typography.sizes['3xl']};
    }

    h3 {
      font-size: ${props => props.theme.typography.sizes['2xl']};
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    .container {
      padding: 0 ${props => props.theme.spacing[3]};
    }

    h1 {
      font-size: ${props => props.theme.typography.sizes['3xl']};
    }

    h2 {
      font-size: ${props => props.theme.typography.sizes['2xl']};
    }

    h3 {
      font-size: ${props => props.theme.typography.sizes.xl};
    }
  }
`;

export default GlobalStyles;
